// Input validation and sanitization utilities
import DOMPurify from 'dompurify';

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

export const validatePhone = (phone: string): boolean => {
  // Brazilian phone format validation
  const phoneRegex = /^\(\d{2}\)\s\d{4,5}-\d{4}$|^\d{10,11}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

export const sanitizeText = (text: string, maxLength: number = 1000): string => {
  return text
    .trim()
    .slice(0, maxLength)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: URLs
    .replace(/on\w+\s*=/gi, ''); // Remove event handlers
};

export const sanitizeHtml = (html: string): string => {
  // Use DOMPurify for comprehensive XSS prevention
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'code', 'pre', 'a'
    ],
    ALLOWED_ATTR: ['href'], // Only allow href for links
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
    FORCE_BODY: false,
    SANITIZE_DOM: true
  });
};

export const sanitizeMarkdown = (content: string): string => {
  // Sanitize markdown content before rendering
  // This removes potential XSS vectors while preserving markdown formatting
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['#text'], // Only allow text nodes
    KEEP_CONTENT: true
  });
};

export const validateName = (name: string): boolean => {
  return name.trim().length >= 2 && name.trim().length <= 100;
};

export const validateRequired = (value: string): boolean => {
  return value.trim().length > 0;
};

export const formatPhone = (phone: string): string => {
  // Format phone number to Brazilian standard
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateContactForm = (data: {
  name: string;
  email: string;
  phone?: string;
  message: string;
}): ValidationResult => {
  const errors: string[] = [];

  if (!validateRequired(data.name)) {
    errors.push('Nome é obrigatório');
  } else if (!validateName(data.name)) {
    errors.push('Nome deve ter entre 2 e 100 caracteres');
  }

  if (!validateRequired(data.email)) {
    errors.push('Email é obrigatório');
  } else if (!validateEmail(data.email)) {
    errors.push('Email inválido');
  }

  if (data.phone && data.phone.trim() && !validatePhone(data.phone)) {
    errors.push('Telefone inválido');
  }

  if (!validateRequired(data.message)) {
    errors.push('Mensagem é obrigatória');
  } else if (data.message.trim().length > 2000) {
    errors.push('Mensagem muito longa (máximo 2000 caracteres)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};